<template>
  <BottomSheet
    v-model="sheetSnap"
    class="elevation-panel-v2"
    :snap-points="snapPoints"
    @update:model-value="onSheetSnapChange"
  >
    <template #header>
      <div class="epv2-header-row">
        <button
          class="epv2-icon-btn"
          type="button"
          :title="sheetSnap === peekKey ? 'Show elevation profile' : (dismissible ? 'Close elevation profile' : 'Hide elevation profile')"
          @click.stop="onMinimizeClick"
        >
          <span class="material-icons">{{ sheetSnap === peekKey ? 'expand_less' : 'expand_more' }}</span>
        </button>

        <!-- Dropped while range-selecting: with the slider active the title
             just crowds out the selection stats — the "Select range" button
             (now reading as Cancel) and the collapse chevron are enough
             chrome on their own. -->
        <span v-if="!rangeSelection.active.value" class="epv2-title">{{ $t('shared.elevation') }}</span>

        <SelectionStatsPanel
          v-if="rangeSelection.stats.value"
          :selection="rangeSelection.stats.value"
          :measurement-pref="pref"
          @reset="onSelectionReset"
        />
        <div v-else class="epv2-stat-cluster">
          <span class="epv2-stat">
            <span class="material-icons">straighten</span>
            {{ formattedHeaderDistance }}
          </span>
          <span class="epv2-stat">
            <span class="material-icons">trending_up</span>
            {{ formattedHeaderGain }}
          </span>
          <span class="epv2-stat">
            <span class="material-icons">trending_down</span>
            {{ formattedHeaderLoss }}
          </span>
          <span class="epv2-stat">
            <span class="material-icons">schedule</span>
            {{ formattedHeaderDuration }}
          </span>
        </div>

        <button
          class="epv2-icon-btn epv2-select-range-btn"
          type="button"
          :class="{ 'epv2-select-range-btn--active': rangeSelection.active.value }"
          :title="rangeSelection.active.value ? 'Cancel selection' : 'Select a range of the route'"
          @click.stop="toggleRangeSelect"
        >
          <span class="material-icons">linear_scale</span>
        </button>
      </div>
    </template>

    <div class="epv2-body" :class="{ 'epv2-body--expanded': isExpanded }">
      <div class="epv2-chart-wrap" :class="{ 'epv2-chart-wrap--expanded': isExpanded }">
        <ElevationPanelChart
          ref="chartRef"
          :data="resolvedData"
          :active-index="scrub.activeIndex.value"
          :selection-range="selectionIndexRange"
          :aria-label="chartAriaLabel"
          :use-surface-line-color="hasMeaningfulSurfaceData"
          @hover="onChartHover"
          @hover-end="onChartHoverEnd"
          @point-click="onChartPointClick"
        />
        <ScrubReadout v-if="!rangeSelection.active.value" :point="scrubPoint" anchor="top" />
      </div>

      <div v-if="rangeSelection.active.value" class="epv2-range-slider-wrap">
        <vue-slider
          v-model="sliderRange"
          :min="0"
          :max="sliderMax"
          :interval="sliderInterval"
          :enable-cross="false"
          :tooltip="'none'"
        />
      </div>

      <p class="visually-hidden">{{ a11ySummary }}</p>
    </div>
  </BottomSheet>
</template>

<script setup lang="ts">
/**
 * elevation-panel-v2 container. Owns bottom-sheet state, the map-marker
 * lifecycle, and orchestration between scrub and range-selection (mutually
 * exclusive: entering range-select suppresses the scrub readout and swaps
 * the header's stat cluster for the selection summary).
 *
 * Deliberately does NOT wrap or extend the original `ElevationProfile.vue` —
 * see the elevation-panel-v2 plan's Architecture section for why. Only the
 * pure-data utils layer (`buildElevationChartData`, `elevationMarker`,
 * `rangeStats`, `steepnessGradient`, `surfaceGroups`) is shared; none of it
 * lives inside `ElevationProfile.vue` itself, so there is zero reuse cost to
 * keeping the component trees separate.
 *
 * Feature-flagged: mount this only when `window.elevationPanelV2Enabled` is
 * true at the call site; `ElevationProfile.vue` and its 7 existing consumers
 * are untouched.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import type mapboxgl from 'mapbox-gl';
import { debounce } from 'lodash-es';
import VueSlider from 'vue-slider-component';
// The base stylesheet is layout-only (positioning/box-model, no colours or
// dot/rail sizing) — without a theme on top, the rail and dots render with no
// visible size or colour at all, i.e. the range slider appears "broken"
// (no visible track/handles). Matches the exact import set already proven
// working in RecordingCropApp.vue, the only other vue-slider-component
// consumer in the codebase.
import 'vue-slider-component/dist-css/vue-slider-component.css';
import 'vue-slider-component/theme/antd.css';
import 'vue-slider-component/theme/default.css';

import BottomSheet, { type SnapPoint } from '../components/BottomSheet.vue';
import ElevationPanelChart from './components/ElevationPanelChart.vue';
import SelectionStatsPanel from './components/SelectionStatsPanel.vue';
import ScrubReadout, { type ScrubPoint } from './components/ScrubReadout.vue';

import { buildElevationChartData, type ElevationChartData } from '../../utils/elevation/buildElevationChartData';
import { MeasurementPrefUtility } from '../../utils/helpers/MeasurementPref';
import { PrettyTime } from '../../utils/helpers/HikeTime';
import { createElevationMarker, type ElevationMarkerController } from '../../utils/elevation/elevationMarker';
import { createRangeHighlight, type RangeHighlightController } from '../../utils/elevation/rangeHighlight';
import { computeSteepnessBuckets } from '../../utils/elevation/steepnessGradient';
import { surfaceGroupForValue, computeSurfaceGroups } from '../../utils/elevation/surfaceGroups';
import { nearestDistanceIndex, sliderStepFor } from '../../utils/elevation/rangeStats';
import { useElevationScrub } from './composables/useElevationScrub';
import { useRangeSelection } from './composables/useRangeSelection';

interface StatsOverride {
  distanceMeters?: number;
  gainMeters?: number;
  lossMeters?: number;
  durationSeconds?: number | null;
}

const props = withDefaults(
  defineProps<{
    mode: 'planned' | 'recording';
    coordinates?: number[][] | null;
    altitudes?: number[] | null;
    distances?: number[] | null;
    surfaces?: number[] | null;
    maxPoints?: number;
    measurementPref?: MeasurementPrefUtility | null;
    getMap?: (() => mapboxgl.Map | null | undefined) | null;
    /** Override the header's whole-route stats with authoritative stored
     *  values (e.g. Recording.distanceMeters) instead of values recomputed
     *  from the (possibly decimated) chart data. */
    stats?: StatsOverride | null;
    /** Snap the sheet starts at. Host pages that mount this unconditionally
     *  alongside the map (e.g. TrailDataCard's recording view) should pass
     *  'peek' so the chart doesn't cover the screen before the user asks for
     *  it; pages that only mount it on an explicit user action (e.g. tapping
     *  hike map's "Elevation" tab) want the chart visible immediately, so
     *  they can leave this at the 'default' fallback. */
    initialSnap?: 'peek' | 'default' | 'expanded';
    /** When true, the header's chevron button fully dismisses the panel
     *  (emits 'dismiss') instead of collapsing to the 'peek' snap. Host pages
     *  that mount this conditionally (e.g. hike map's mobile "Elevation" tab,
     *  which v-if's the whole sheet away) want a real close; pages that keep
     *  it permanently mounted (e.g. TrailDataCard) have no unmount to fall
     *  back to, so they should leave this false and keep the peek behavior. */
    dismissible?: boolean;
  }>(),
  {
    coordinates: null,
    altitudes: null,
    distances: null,
    surfaces: null,
    maxPoints: 300,
    measurementPref: null,
    getMap: null,
    stats: null,
    initialSnap: 'default',
    dismissible: false,
  }
);

const emit = defineEmits<{
  /** Fires on mount (with the initial snap) and on every subsequent change.
   *  Lets a host page (e.g. TrailDataCard.vue's mobile tab bar) avoid
   *  rendering its own fixed-position chrome on top of the sheet when it's
   *  not collapsed to 'peek'. */
  (e: 'snap-change', key: string): void;
  /** Only fires when `dismissible` is true — see the prop's doc comment. */
  (e: 'dismiss'): void;
}>();

const PEEK_KEY = 'peek';
const DEFAULT_KEY = 'default';
const EXPANDED_KEY = 'expanded';
const peekKey = PEEK_KEY;

const snapPoints: SnapPoint[] = [
  { key: PEEK_KEY, heightPx: 88 },
  // Content is now a fixed size (header + 180px chart — the ribbon strips
  // and legend that used to live below the chart are gone, since
  // steepness/surface are baked into the chart's fill/line colour instead),
  // so size to content rather than a viewport fraction: a vh-based height
  // leaves growing dead space below the content as the viewport gets taller.
  { key: DEFAULT_KEY, heightPx: 'auto' },
  { key: EXPANDED_KEY, heightPx: '92vh' },
];

const INITIAL_SNAP_KEYS = { peek: PEEK_KEY, default: DEFAULT_KEY, expanded: EXPANDED_KEY } as const;
const sheetSnap = ref(INITIAL_SNAP_KEYS[props.initialSnap]);
// Only the 'expanded' snap gets the flexible chart-fills-available-space
// treatment (see `.epv2-chart-wrap--expanded` below) — kept as an opt-in
// modifier class rather than the chart-wrap's default styling because
// Chart.js measures its container synchronously at construction time, and a
// `flex: 1` height (resolved by the flex algorithm, one layout pass later
// than a plain fixed height) made that initial measurement land on Chart.js's
// built-in 150px fallback instead of the real 180px on first mount.
const isExpanded = computed(() => sheetSnap.value === EXPANDED_KEY);

const pref = computed(() => props.measurementPref ?? MeasurementPrefUtility.preference());

const resolvedData = computed<ElevationChartData>(() =>
  buildElevationChartData({
    coordinates: props.coordinates ?? [],
    altitudes: props.altitudes ?? undefined,
    distances: props.distances ?? undefined,
    surfaces: props.surfaces ?? undefined,
    maxPoints: props.maxPoints,
    measurementPref: pref.value,
  })
);

// Still used by the accessible name and summary below, which keep naming the
// mode for screen readers even though the visible chip it once labelled has
// been removed from the header.
const contextLabel = computed(() => (props.mode === 'planned' ? 'Planned route' : 'Your recording'));

// --- Surface line-colour graceful degradation ------------------------------
//
// A near-entirely-"Unknown" surface line (common: plenty of routes lack good
// OSM surface tagging) adds visual noise without conveying information. Only
// colour the chart's line by surface when there's meaningful coverage —
// otherwise it degrades to the flat brand-gold line, which is a fine,
// unsurprising fallback since it's fixed once from the route's own static
// data, never toggled live.
const MIN_KNOWN_SURFACE_RATIO = 0.3; // i.e. fall back once >70% of points are "Unknown"

const knownSurfaceRatio = computed(() => {
  const surfaces = resolvedData.value.surfaces;
  if (!surfaces.length) return 0;
  const groups = computeSurfaceGroups(surfaces);
  const knownCount = groups.filter((g) => g !== 'unknown').length;
  return knownCount / groups.length;
});

const hasMeaningfulSurfaceData = computed(
  () => props.mode === 'planned' && knownSurfaceRatio.value >= MIN_KNOWN_SURFACE_RATIO
);

// --- Header stats (whole route, or the stored/authoritative override) -----

const headerDistanceMeters = computed(() => props.stats?.distanceMeters ?? resolvedData.value.maxDistanceMeters);
const headerGainMeters = computed(() => props.stats?.gainMeters ?? resolvedData.value.elevationGainMeters);
// Hosts with no stored descent figure pass `lossMeters: null` (e.g.
// TrailDataCard), so this falls through to the value the shared builder
// already computes with the same 6m-threshold algorithm as the gain.
const headerLossMeters = computed(() => props.stats?.lossMeters ?? resolvedData.value.elevationLossMeters);
const headerDurationSeconds = computed(() => props.stats?.durationSeconds ?? 0);

// 1dp so this agrees with the stages footer's total on the hike page.
const formattedHeaderDistance = computed(() => pref.value.bigUnitFormatter(headerDistanceMeters.value / 1000, true));
const formattedHeaderGain = computed(() => pref.value.smallUnitFormatter(headerGainMeters.value, false));
const formattedHeaderLoss = computed(() => pref.value.smallUnitFormatter(headerLossMeters.value, false));
const formattedHeaderDuration = computed(() => new PrettyTime(headerDurationSeconds.value).prettyTime());

// --- Map marker -------------------------------------------------------

let markerController: ElevationMarkerController | null = null;
let highlightController: RangeHighlightController | null = null;

// --- Scrub --------------------------------------------------------------

const scrub = useElevationScrub();
let hasFiredScrubEvent = false;

function resolveLngLat(index: number): [number, number] | null {
  const d = resolvedData.value;
  const lon = d.lons[index];
  const lat = d.lats[index];
  if (lon == null || lat == null) return null;
  return [lon, lat];
}

function resolveScrubPoint(index: number): ScrubPoint | null {
  const d = resolvedData.value;
  if (index < 0 || index >= d.points.length) return null;
  return {
    distanceLabel: pref.value.bigUnitFormatter(d.distancesMeters[index] / 1000, true),
    // points[i].y is already converted to the display unit by
    // buildElevationChartData — format it directly rather than re-running it
    // through smallUnitFormatter (which expects raw metres) a second time.
    elevationLabel: `${Math.round(d.points[index].y)}${d.unitLabels.elevation}`,
    gradientPercent: d.gradients[index] ?? 0,
    surfaceLabel: hasMeaningfulSurfaceData.value ? surfaceGroupForValue(d.surfaces[index]).label : undefined,
    surfaceColor: hasMeaningfulSurfaceData.value ? surfaceGroupForValue(d.surfaces[index]).color : undefined,
  };
}

const scrubPoint = computed<ScrubPoint | null>(() =>
  scrub.activeIndex.value != null ? resolveScrubPoint(scrub.activeIndex.value) : null
);

function onChartHover(payload: { index: number }): void {
  if (rangeSelection.active.value) return;
  scrub.setActiveIndex(payload.index);
  const lngLat = resolveLngLat(payload.index);
  if (lngLat) markerController?.show(lngLat);
  if (!hasFiredScrubEvent) {
    hasFiredScrubEvent = true;
    window.mixpanel?.track('Elevation Panel Scrubbed', { Mode: props.mode });
  }
}

function onChartHoverEnd(): void {
  scrub.clear();
  markerController?.hide();
  hasFiredScrubEvent = false;
}

function onChartPointClick(_payload: { index: number }): void {
  // Reserved for a future "insert waypoint here"-style action; no-op for the pilot.
}

// --- Range selection ------------------------------------------------------

const rangeSelection = useRangeSelection({
  coordinates: computed(() => props.coordinates ?? []),
  // Full-resolution, parallel to `coordinates` — NOT the decimated chart
  // `resolvedData.distancesMeters` (parallel to `points`, capped at
  // `maxPoints`), which would silently mismatch lengths and force
  // computeRangeStats' zero-stats guard on every real route.
  distancesMeters: computed(() => resolvedData.value.fullDistancesMeters),
  altitudes: computed(() => props.altitudes ?? undefined),
});

// See sliderStepFor's docs — vue-slider-component requires `(max - min)` to
// be an exact multiple of `interval`, which a naive `maxDistanceMeters / 200`
// almost never satisfies once floating-point division round-trips.
const sliderStep = computed(() => sliderStepFor(resolvedData.value.maxDistanceMeters));
const sliderMax = computed(() => sliderStep.value.max);
const sliderInterval = computed(() => sliderStep.value.interval);

const sliderRange = ref<[number, number]>([0, 0]);

watch(
  () => rangeSelection.active.value,
  (active) => {
    if (active) {
      sliderRange.value = [rangeSelection.startMeters.value ?? 0, rangeSelection.endMeters.value ?? 0];
      scrub.clear();
      markerController?.hide();
    }
  }
);

function toggleRangeSelect(): void {
  if (rangeSelection.active.value) {
    rangeSelection.exit();
  } else {
    rangeSelection.enter();
  }
}

// The slider's v-model fires continuously during a drag (matches
// RecordingCropApp.vue's `:lazy="false"` behaviour). Recomputing RangeStats
// (elevation gain/loss over the slice) on every tick would be wasteful on a
// long route, so the expensive commit is debounced 100ms — the same
// precedent already used for the crop tool's `debouncedRange`. The visual
// selection shading (`selectionIndexRange`, below) stays keyed off the live
// `sliderRange` so the chart still tracks the finger/cursor instantly.
let hasFiredRangeSelectedEvent = false;
const debouncedCommitRange = debounce((start: number, end: number) => {
  rangeSelection.setRange(start, end);
  if (!hasFiredRangeSelectedEvent) {
    hasFiredRangeSelectedEvent = true;
    window.mixpanel?.track('Elevation Panel Range Selected', { Mode: props.mode });
  }
}, 100);

watch(sliderRange, (value) => debouncedCommitRange(value[0], value[1]));

function onSelectionReset(): void {
  hasFiredRangeSelectedEvent = false;
  rangeSelection.exit();
}

// Mirrors the chart's own `selectionIndexRange` below: keyed off the live
// (undebounced) `sliderRange` against FULL-RESOLUTION coordinates/distances
// (not the decimated chart data) so the on-map highlight is the actual route
// geometry for the selection, not an approximation from downsampled points.
const highlightCoordinates = computed<number[][]>(() => {
  if (!rangeSelection.active.value) return [];
  const coords = props.coordinates ?? [];
  const distances = resolvedData.value.fullDistancesMeters;
  if (coords.length < 2 || distances.length !== coords.length) return [];
  const lowMeters = Math.min(sliderRange.value[0], sliderRange.value[1]);
  const highMeters = Math.max(sliderRange.value[0], sliderRange.value[1]);
  const startIndex = nearestDistanceIndex(distances, lowMeters);
  const endIndex = nearestDistanceIndex(distances, highMeters);
  if (endIndex <= startIndex) return [];
  return coords.slice(startIndex, endIndex + 1);
});

watch(
  highlightCoordinates,
  (coords) => {
    if (coords.length >= 2) {
      highlightController?.show(coords);
    } else {
      highlightController?.hide();
    }
  },
  { immediate: true }
);

function nearestIndexForMeters(distancesMeters: number[], meters: number): number {
  let closest = 0;
  let minDiff = Infinity;
  for (let i = 0; i < distancesMeters.length; i++) {
    const diff = Math.abs(distancesMeters[i] - meters);
    if (diff < minDiff) {
      minDiff = diff;
      closest = i;
    }
  }
  return closest;
}

const selectionIndexRange = computed(() => {
  if (!rangeSelection.active.value) return null;
  const distances = resolvedData.value.distancesMeters;
  if (!distances.length) return null;
  return {
    startIndex: nearestIndexForMeters(distances, sliderRange.value[0]),
    endIndex: nearestIndexForMeters(distances, sliderRange.value[1]),
  };
});

// --- Bottom sheet ---------------------------------------------------------

function onSheetSnapChange(key: string): void {
  emit('snap-change', key);
  if (key === EXPANDED_KEY) {
    window.mixpanel?.track('Elevation Panel Fullscreen Opened', { Mode: props.mode });
  }
}

function onMinimizeClick(): void {
  // Dismissible hosts (e.g. hike map's mobile "Elevation" tab): the down
  // chevron (shown whenever we're not already at peek) fully closes the
  // panel instead of shrinking to peek — the host unmounts the whole sheet
  // on 'dismiss', so there's nothing to peek back to.
  if (props.dismissible && sheetSnap.value !== PEEK_KEY) {
    emit('dismiss');
    return;
  }
  // Assigning `sheetSnap.value` directly does NOT trigger BottomSheet's own
  // `update:modelValue` emit — that only fires from BottomSheet's internal
  // drag/tap-on-grip handlers, not when the parent changes the bound ref
  // itself. Without this explicit call, a host page listening on
  // `@snap-change` (e.g. TrailDataCard.vue's mobile "Details" tab, which is
  // only meant to show while the sheet is peeked) never finds out the sheet
  // left peek via this button, and stays visibly stuck mid-chart.
  sheetSnap.value = sheetSnap.value === PEEK_KEY ? DEFAULT_KEY : PEEK_KEY;
  onSheetSnapChange(sheetSnap.value);
}

// --- Accessibility ----------------------------------------------------

const chartAriaLabel = computed(
  () =>
    `Elevation chart, ${contextLabel.value}. ${formattedHeaderDistance.value}, ${formattedHeaderGain.value} ascent.`
);

const a11ySummary = computed(() => {
  const parts: string[] = [
    `Elevation profile for ${contextLabel.value.toLowerCase()}.`,
    `Distance ${formattedHeaderDistance.value}, elevation gain ${formattedHeaderGain.value}, estimated duration ${formattedHeaderDuration.value}.`,
  ];

  const buckets = computeSteepnessBuckets(resolvedData.value.gradients);
  const distances = resolvedData.value.distancesMeters;
  const steepSections: string[] = [];
  let runStart: number | null = null;
  for (let i = 0; i <= buckets.length; i++) {
    const isSteep = i < buckets.length && buckets[i] === 'verySteep';
    if (isSteep && runStart == null) {
      runStart = i;
    } else if (!isSteep && runStart != null) {
      const startMeters = distances[runStart];
      const endMeters = distances[i - 1];
      if (endMeters - startMeters >= 100 && steepSections.length < 2) {
        steepSections.push(
          `${pref.value.bigUnitFormatter(startMeters / 1000, true)} to ${pref.value.bigUnitFormatter(endMeters / 1000, true)}`
        );
      }
      runStart = null;
    }
  }
  if (steepSections.length) {
    parts.push(`Steepest sections: ${steepSections.join(', ')}.`);
  }

  return parts.join(' ');
});

// --- Lifecycle --------------------------------------------------------

onMounted(() => {
  if (props.getMap) {
    markerController = createElevationMarker(() => props.getMap?.() ?? null);
    highlightController = createRangeHighlight(() => props.getMap?.() ?? null);
    if (highlightCoordinates.value.length >= 2) highlightController.show(highlightCoordinates.value);
  }
  emit('snap-change', sheetSnap.value);
  window.mixpanel?.track('Elevation Panel Viewed', { Mode: props.mode });
});

onBeforeUnmount(() => {
  markerController?.destroy();
  highlightController?.destroy();
});
</script>

<style scoped>
.elevation-panel-v2 {
  color: #fff;
}

/* Wraps all header controls in their own row so they lay out consistently
   (a single wrapping flex row) on both desktop, where BottomSheet's own
   `.bottom-sheet-desktop-header` is already a flex row, and mobile, where
   `.bottom-sheet-handle` stacks its direct children VERTICALLY (grip, then
   header content) — without this wrapper, the chevron/title/chip/stats/
   select-range-button were each their own direct child of that column
   layout, so they stacked as five separate rows instead of one wrapping
   row, blowing out the header's real height far past the fixed 88px "peek"
   snap and getting clipped mid-title by the sheet's `overflow: hidden`. */
.epv2-header-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
  min-width: 0;
}

.epv2-icon-btn {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
}

.epv2-icon-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.epv2-select-range-btn--active {
  background: rgba(255, 223, 70, 0.2);
  color: #ffdf46;
}

.epv2-title {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
}

.epv2-stat-cluster {
  /* An explicit (non-zero) flex-basis so the wrap decision in
     `.epv2-header-row` treats this as one ~220px-wide unit and moves it
     whole onto the next line when the row is too narrow (mobile) — with
     `flex: 1` (a 0 basis), the line-breaking algorithm always placed it on
     line 1 since a 0-basis item "fits" anywhere, and only the subsequent
     shrink pass squeezed it down, so its 3 stats wrapped internally into an
     unreadable, near-vertical sliver instead of the whole cluster wrapping
     to its own full-width row. */
  flex: 1 1 220px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  min-width: 0;
  justify-content: flex-end;
}

.epv2-stat {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.9);
  white-space: nowrap;
}

.epv2-stat .material-icons {
  font-size: 15px;
  color: #ffdf46;
}

.epv2-body {
  padding: 8px 10px 4px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

/* Only applied for the 'expanded' snap (a fixed 92vh) so `.epv2-chart-wrap`
   below has genuine extra space to grow into via `flex: 1`. Not the default
   styling — see `.epv2-chart-wrap`'s own comment for why. */
.epv2-body--expanded {
  height: 100%;
  min-height: 0;
}

/* 150px, not the fallback ElevationProfile's 180px: V2's header (title +
   context chip + always-visible distance/gain/duration stats, ~46-58px) adds
   height the fallback's compact inline title+subtitle doesn't, so matching
   the CHART height exactly would leave the whole panel noticeably taller
   overall than the fallback (as seen on hike map / recording map vs the
   planner, which only ever renders the fallback). Trimming the chart brings
   the total default-snap footprint back in line with the fallback's ~180px. */
.epv2-chart-wrap {
  position: relative;
  height: 120px;
}

/* Opt-in only for the 'expanded' snap, not the chart-wrap's default styling:
   Chart.js measures its container synchronously when the chart is
   constructed. A `flex: 1` height is resolved by the flex algorithm one
   layout pass later than a plain fixed `height`, and that gap made Chart.js's
   *initial* mount-time measurement land on its own built-in 150px fallback
   instead of the real 180px — leaving a permanent 30px dead gap below the
   chart even outside the expanded snap, since nothing then told Chart.js to
   re-measure. Restricting the flexible sizing to this modifier class (added
   only once the user actually expands, a real later resize the chart's
   ResizeObserver reliably catches) keeps the default snap's chart mount
   exactly as it was before the 'expanded' fill feature existed. */
.epv2-chart-wrap--expanded {
  height: auto;
  flex: 1;
  min-height: 180px;
}

.epv2-range-slider-wrap {
  padding: 4px 10px;
}

/* Recolour the antd theme's default light-blue accents to the panel's own
   gold, matching the elevation line / steepness fill / context chip elsewhere
   in this component (same override RecordingCropApp.vue applies to its own
   dark-background slider). */
.epv2-range-slider-wrap :deep(.vue-slider-process) {
  background-color: #ffdf46;
}

.epv2-range-slider-wrap :deep(.vue-slider-dot-handle) {
  border-color: #ffdf46;
}

.epv2-range-slider-wrap :deep(.vue-slider:hover .vue-slider-dot-handle),
.epv2-range-slider-wrap :deep(.vue-slider-dot-handle:hover),
.epv2-range-slider-wrap :deep(.vue-slider-dot-handle-focus) {
  border-color: #e6c93e;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
